import { useLayoutEffect, useRef, useState } from 'react';

import { QuickActionsBlock } from '@crm/widgets/deal-details-content/ui/QuickActionsBlock';
import { DealNotes } from '@crm/widgets/deal-notes';
import { TaskBlock } from '@crm/widgets/deal-tasks';

import { InformationBlock } from './ui/InformationBlock';

import type { TDealDetails } from '@crm/entities/deal';

interface DealDetailsContentProps {
    dealDetailsData: TDealDetails;
}

export const DealDetailsContent = ({ dealDetailsData }: DealDetailsContentProps) => {
    const informationColumnRef = useRef<HTMLDivElement | null>(null);
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const limitedColumnStyle = contentHeight ? { height: contentHeight } : undefined;

    useLayoutEffect(() => {
        const informationColumn = informationColumnRef.current;

        if (!informationColumn) {
            return undefined;
        }

        const updateContentHeight = () => {
            setContentHeight(informationColumn.getBoundingClientRect().height);
        };

        updateContentHeight();

        const resizeObserver = new ResizeObserver(updateContentHeight);
        resizeObserver.observe(informationColumn);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div className="flex items-start gap-4">
            <div
                ref={informationColumnRef}
                className="max-w-[350px] min-w-[300px] flex-[0_1_500px]"
            >
                <InformationBlock
                    key={dealDetailsData.dealData.id}
                    dealDetailsData={dealDetailsData}
                />
            </div>
            <div className="flex-1 overflow-hidden [&>section]:h-full" style={limitedColumnStyle}>
                <DealNotes
                    dealId={dealDetailsData.dealData.id}
                    messagesData={dealDetailsData.communicationData?.comments ?? []}
                    stateToken={dealDetailsData.communicationData?.stateToken}
                />
            </div>
            <div
                className="flex max-w-[500px] min-w-[400px]  shrink-0 flex-col gap-4 overflow-hidden"
                style={limitedColumnStyle}
            >
                <TaskBlock
                    className="min-h-0 flex-1"
                    dealId={dealDetailsData.dealData.id}
                    tasksData={dealDetailsData.tasksData}
                />
                <QuickActionsBlock dealDetailsData={dealDetailsData} />
            </div>
        </div>
    );
};
